import './dataSources.less'
import {
  ACTION_DATA_SOURCES_STRUCTURED_SUMMARY,
  StructuredSummaryWidget,
  fetchDataSourcesStructuredSummary as fetchWidget,
  resetStructuredSummaryWidget as resetWidget,
  DataSourceSummaryWidgetParams
} from './dataSourcesSlice'
import { getShowLoader } from '../../reducers/requestReducer'
import { RootState } from '../../rootReducer'
import LoaderView from '../../components/Loader/loader'
import useUrlContext from '../../hooks/useUrlContext'

import {
  DATA_SOURCE_ID,
  URL_TABLES,
  URL_DOCUMENTS_ALL,
  URL_INSIGHTS,
  FILTER_DATASOURCE_CATEGORY,
  FILTER_STRUCTURED_KEY,
  DATA_SOURCE_TYPES,
  DATA_SOURCE_TYPE
} from '../../constants'
import { getDataSourceBasePath } from '../../utils/dataSourceUtil'
import NoSensitiveDataSvg from '../../assets/inlineSvg/noData.svg'
import ServerIcon from '../../assets/inlineSvg/serverIcon.svg'
import { getUpdatedUrl } from '../../utils/urlUtil'
import graphqlService from '../../services/graphqlService'
import { mockStructuredSummaryWidgetData } from '../dashboards/renderer/utils/widgetMockdata'
import { Typography } from '@lightbeamai/design-system'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import './structuredSummaryWidget.less'

interface OwnProps {
  useMockData?: boolean
  dataSourceId?: string
  legendPosition?: 'bottom' | 'right'
  parentUrl?: string
  className?: string
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDashboardType?: DATA_SOURCE_TYPES
  widgetDatasourceType?: DATA_SOURCE_TYPES
  disableLinks?: boolean
}

interface StateProps {
  widget?: { [key: string]: StructuredSummaryWidget }
  showLoader: boolean
}

interface DispatchProps {
  fetchWidget: (params: DataSourceSummaryWidgetParams) => void
  resetWidget: () => void
  disableLinks?: boolean
}

type StructuredSummaryComponentProps = StateProps & DispatchProps & OwnProps

const StructuredSummaryWidget = (props: StructuredSummaryComponentProps): React.ReactElement => {
  const {
    dataSourceId: dataSourceIdProp = '',
    legendPosition,
    widget,
    showLoader,
    parentUrl = '',
    className = '',
    useMockData = false,
    widgetDatasourceId,
    widgetDatasourceType,
    disableLinks = false,
    fetchWidget,
    resetWidget
  } = props

  const intl = useIntl()
  const context = useUrlContext()
  const dataSourceIdFromUrl = context.contextFilters[DATA_SOURCE_ID]
  const dataSourceId = widgetDatasourceId
    ? widgetDatasourceId
    : dataSourceIdFromUrl || dataSourceIdProp

  const data =
    useMockData || !dataSourceId ? widget && widget['ALL'] : widget && widget[dataSourceId]

  const { databasesCount = 0, tablesCount = 0, totalClusterCount = 0 } = data || {}

  const isWidgetData = data && Object.values(data).some((val) => val > 0)
  const urlParams = `?${encodeURIComponent(
    URL_DOCUMENTS_ALL
  )}=${FILTER_DATASOURCE_CATEGORY}%3A${FILTER_STRUCTURED_KEY}`

  useEffect(() => {
    if (useMockData) return
    fetchWidget({ datasourceId: dataSourceId })
    return () => {
      graphqlService.abort(ACTION_DATA_SOURCES_STRUCTURED_SUMMARY)
      resetWidget()
    }
  }, [dataSourceId, fetchWidget, resetWidget, useMockData])

  let linkTables = parentUrl + URL_INSIGHTS + URL_DOCUMENTS_ALL + urlParams

  const pathBaseString = getDataSourceBasePath(widgetDatasourceType, dataSourceId, context.pathname)

  if (dataSourceId) {
    linkTables = getUpdatedUrl({
      pathName: pathBaseString + URL_TABLES,
      pageName: URL_TABLES,
      queryParams: {
        [DATA_SOURCE_TYPE]: widgetDatasourceType || '',
        [DATA_SOURCE_ID]: dataSourceId || ''
      }
    })
  }
  return (
    <div className="lb-widget-card structured-summary xs-p-16">
      <div
        className={classNames('data-sources-summary widget', {
          [className]: !!className,
          'legend-pos-right': legendPosition === 'right'
        })}
        data-test-id="datasources-structured-summary-widget"
      >
        <div>
          <Typography color="primary" variant="h6">
            {intl.formatMessage({ id: 'dataSources.widget.structured.title' })}
          </Typography>
        </div>
        <div
          className={classNames('data-sources-summary', {
            [className]: !!className,
            'legend-pos-right': legendPosition === 'right'
          })}
        >
          {isWidgetData ? (
            <div className="content-container">
              <Link to={disableLinks ? undefined : linkTables} className="total">
                <Typography className="xs-mb-0" color="primary" type="strong" variant="basePlus6">
                  {intl.formatNumber(tablesCount, { notation: 'compact' })}
                </Typography>
                <Typography color="primary" type="medium" variant="baseMinus2">
                  {intl.formatMessage({ id: 'dataSources.widget.structured.tables' })}
                </Typography>
              </Link>
              <div className="totals">
                <div className="item">
                  <div className="item-text-wrapper">
                    <div className="text-icon">
                      <ServerIcon />
                    </div>
                    <Typography color="primary" type="medium" variant="baseMinus2">
                      {intl.formatMessage({ id: 'dataSources.widget.structured.databases' })}
                    </Typography>
                  </div>
                  <Typography
                    className="xs-mb-0"
                    color="primary"
                    type="strong"
                    variant="baseMinus2"
                  >
                    {intl.formatNumber(databasesCount, { notation: 'compact' })}
                  </Typography>
                </div>
                <div className="item">
                  <div className="item-text-wrapper">
                    <div className="text-icon">
                      <ServerIcon />
                    </div>
                    <Typography color="primary" type="medium" variant="baseMinus2">
                      {intl.formatMessage({ id: 'dataSources.widget.structured.clusters' })}
                    </Typography>
                  </div>

                  <Typography
                    className="xs-mb-0"
                    color="primary"
                    type="strong"
                    variant="baseMinus2"
                  >
                    {intl.formatNumber(totalClusterCount, { notation: 'compact' })}
                  </Typography>
                </div>
              </div>
            </div>
          ) : (
            <div className="entity-empty-state">
              <div className="icon-box xs-mb-12">
                <NoSensitiveDataSvg />
              </div>
              <Typography
                color="placeholder"
                type="medium"
                variant="baseMinus2"
                className="xs-mt-16"
              >
                {intl.formatMessage({ id: 'dashboard.widget.noSensitiveData' })}
              </Typography>
            </div>
          )}
        </div>
        <LoaderView showLoader={showLoader} size="small" />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? mockStructuredSummaryWidgetData
    : state.dataSources.structuredSummaryWidget,
  showLoader: getShowLoader(ACTION_DATA_SOURCES_STRUCTURED_SUMMARY)
})

const mapDispatchToProps: DispatchProps = { fetchWidget, resetWidget }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(StructuredSummaryWidget)
